import { MenuItem } from '@mui/material';
import { Stack } from '@mui/system';
import { FC, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { IntlShape, useIntl } from 'react-intl';
import { useTypedForm } from '../../../../../../../shared/fe/hooks/useForm';
import { LoadableSelect } from '../../../../../form/LoadableSelect';
import { useAuthSignedIn } from '../../../../../auth/useAuth';

const formSchema = (intl: IntlShape) =>
	z.object({
		organizationSlug: z.string(),
	});

type FormValues = z.infer<ReturnType<typeof formSchema>>;

const SelectOrganizationForm: FC = () => {
	const intl = useIntl();
	const { me } = useAuthSignedIn();

	const currentSlug = me.person?.organizations.find((organization) => organization.isCurrent)?.slugPreferred;

	const { handleSubmit, setValue, control, reset } = useTypedForm({
		schema: formSchema(intl),
		defaultValues: {
			organizationSlug: '', // value cannot be undefined in order to keep the input controlled
		},
	});

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (me != null) {
			reset({ organizationSlug: currentSlug });
		}
	}, [me, currentSlug, reset, setValue]);

	const onSubmit = (data: FormValues) => {
		const protocol = window.location.protocol;
		const parsedHost = window.location.host.split('.');

		parsedHost[0] = data.organizationSlug;

		window.location.assign(protocol + '//' + parsedHost.join('.'));
	};

	return (
		<form style={{ width: '100%' }}>
			<Stack justifyContent={'center'} alignItems={'center'} m={'0 12px'}>
				<Controller
					name={'organizationSlug'}
					control={control}
					render={({ field: { value } }) => (
						<LoadableSelect
							sx={{ width: '100%' }}
							value={value}
							loading={!me}
							onChange={(e) => {
								setValue('organizationSlug', e.target.value);
								handleSubmit(onSubmit)();
							}}
						>
							{me.person?.organizations.map((organization) => (
								<MenuItem key={organization.slugPreferred} value={organization.slugPreferred}>
									{organization.name}
								</MenuItem>
							))}
						</LoadableSelect>
					)}
				/>
			</Stack>
		</form>
	);
};

export { SelectOrganizationForm };
